import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import * as func from "../common/manipurCommon/FunctionCalls";
import { withoutHeaderRoutes } from "../router/router.link";

const AuthManager = () => {
  const location = useLocation();
  const currentPath = location.pathname;

  // Extract paths from withoutHeaderRoutes to create public paths
  const publicPaths = withoutHeaderRoutes.map((route) => route.path);
  const isPublicPath = publicPaths.includes(currentPath);
  const roleType = "/manipur/login";

  const logoutTimeSeconds = parseInt(localStorage.getItem("closeConfigValue") || "1800", 10);
  const tokenRefreshThresholdSeconds = parseInt(localStorage.getItem("refreshConfigValue") || "1800", 10);

  const logoutTime = logoutTimeSeconds * 1000;
  const tokenRefreshThreshold = tokenRefreshThresholdSeconds * 1000;
  const tokenCheckInterval = 60 * 1000;

  const activityTimeoutRef = useRef<number | null>(null);
  const tokenCheckTimeoutRef = useRef<number | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const logoutUser = useCallback(() => {
    localStorage.clear();
    window.location.href = roleType;
  }, [roleType]);

  const refreshToken = useCallback(async () => {
    try {
      const response = await func.RefreshToken();

      if (response) {
        localStorage.setItem("accessToken", response);
        const now = Date.now();
        localStorage.setItem("tokenIssuedAt", now.toString());
        lastActivityRef.current = now;
        localStorage.setItem("lastActivityAt", now.toString());
      } else {
        logoutUser();
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
      logoutUser();
    }
  }, [logoutUser]);

  const resetActivityTimer = useCallback(() => {
    if (activityTimeoutRef.current !== null) {
      clearTimeout(activityTimeoutRef.current);
    }

    activityTimeoutRef.current = window.setTimeout(() => {
      logoutUser();
    }, logoutTime);
    console.log("[AuthManager] Idle timer reset. Logout after", logoutTime / 1000, "seconds of inactivity.");
  }, [logoutTime, logoutUser]);

  const updateActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    localStorage.setItem("lastActivityAt", now.toString());
    resetActivityTimer();
    console.log("[AuthManager] User activity detected. Activity timestamp updated.");
  }, [resetActivityTimer]);

  useEffect(() => {
    if (isPublicPath) return;

    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      window.location.href = roleType;
      return;
    }

    const storedActivity = parseInt(localStorage.getItem("lastActivityAt") || "", 10);
    if (!isNaN(storedActivity)) {
      lastActivityRef.current = storedActivity;
    } else {
      const now = Date.now();
      lastActivityRef.current = now;
      localStorage.setItem("lastActivityAt", now.toString());
    }

    resetActivityTimer();

    const runTokenCheck = () => {
      const now = Date.now();
      const idleTime = now - lastActivityRef.current;
      const tokenIssuedAt = parseInt(localStorage.getItem("tokenIssuedAt") || "", 10) || now;
      const tokenAge = now - tokenIssuedAt;

      console.log(
        `[AuthManager] Token check: idle ${Math.floor(idleTime / 1000)}s, token age ${Math.floor(tokenAge / 1000)}s`
      );

      if (idleTime >= logoutTime) {
        console.log("[AuthManager] User idle for 15 minutes, logging out.");
        logoutUser();
        return;
      }

      if (tokenAge >= tokenRefreshThreshold) {
        console.log("[AuthManager] Token age reached threshold, refreshing token now.");
        refreshToken();
      }

      tokenCheckTimeoutRef.current = window.setTimeout(runTokenCheck, tokenCheckInterval);
    };

    console.log("[AuthManager] Starting 60-second token check cycle.");
    tokenCheckTimeoutRef.current = window.setTimeout(runTokenCheck, tokenCheckInterval);

    const events = ["click", "mousemove", "keypress", "touchstart", "scroll"];
    events.forEach((event) => window.addEventListener(event, updateActivity));

    return () => {
      if (activityTimeoutRef.current !== null) clearTimeout(activityTimeoutRef.current);
      if (tokenCheckTimeoutRef.current !== null) clearTimeout(tokenCheckTimeoutRef.current);
      events.forEach((event) => window.removeEventListener(event, updateActivity));
    };
  }, [currentPath, isPublicPath, logoutTime, logoutUser, refreshToken, resetActivityTimer, updateActivity, roleType]);

  return null;
};

export default AuthManager;